import { BoldTextM, BorderRadius, Color, Space } from "@enxcs/dap-ui-stratos";
import styled, { css } from "styled-components";
import type { StepperStepStatus } from ".";

export const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  transition: all 0.3s ease-in-out;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${Space.V4};

  & > li:first-child button::before {
    border-top-left-radius: ${BorderRadius.V4};
    border-bottom-left-radius: ${BorderRadius.V4};
  }

  & > li:last-child button::before {
    border-top-right-radius: ${BorderRadius.V4};
    border-bottom-right-radius: ${BorderRadius.V4};
  }
`;

export const StepItem = styled.li`
  min-width: 0;
  max-height: 44px;
  height: 44px;
  align-items: flex-end;
`;

export const StepButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !["$status"].includes(prop),
})<{ $status: StepperStepStatus }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  padding: 0 ${Space.V12};
  padding-top: ${Space.V16};
  background-color: transparent;
  transition: all 0.3s ease-in-out;
  justify-content: center;
  gap: ${Space.V12};
  align-items: center;
  text-align: left;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    transition: all 0.3s ease-in-out;
    background-color: ${({ $status }) => ($status === "upcoming" ? Color.Neutral.V40 : Color.Teal.V30)};
  }
`;

export const StepIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => !["$status"].includes(prop),
})<{ $status: StepperStepStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;
  gap: ${Space.V8};
  svg {
    transition: all 0.3s ease-in-out;
    opacity: ${({ $status }) => ($status === "upcoming" ? 0 : 1)};
  }
`;

export const StepIndicatorText = styled.span`
  font-size: 12px;
  font-weight: 700;
  transition: all 0.3s ease-in-out;

  line-height: 1;
`;

export const StepTitle = styled(BoldTextM).withConfig({
  shouldForwardProp: (prop) => !["$status"].includes(prop),
})<{ $status: StepperStepStatus }>`
  transition: all 0.3s ease-in-out;
  color: ${({ $status }) => ($status === "upcoming" ? Color.Neutral.V50 : Color.Teal.V30)};
`;

export const StepPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !["$scrollable"].includes(prop),
})<{ $scrollable?: boolean }>`
  min-width: 0;

  ${({ $scrollable }) =>
    $scrollable &&
    css`
      max-height: calc(100vh - 400px);
      overflow-y: auto;
      overflow-x: hidden;
      box-sizing: border-box;

      /* Reserves real layout space for the scrollbar (rather than the manual padding-right
         this replaced) so macOS's overlay scrollbar can't render on top of the content. */
      scrollbar-gutter: stable;
      padding-inline-end: ${Space.V8};

      /* Thin scrollbar, matching CustomOrderedListWithSpacing in MeetingRecordContent.styles.ts */
      scrollbar-width: thin;
      scrollbar-color: ${Color.Neutral.V40} transparent;

      &::-webkit-scrollbar {
        width: ${Space.V8};
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: ${Color.Neutral.V40};
        border-radius: ${Space.V8};
        background-clip: padding-box;
      }

      &::-webkit-scrollbar-button:vertical {
        display: none;
        height: 0;
        width: 0;
      }
    `}
`;
